import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const cache = new Map<string, number[]>();
const datedCache = new Map<string, Array<{ date: string; close: number }>>();

/**
 * Batch-fetch close-price series for a set of tickers over the past N days.
 * Results are memoized in a module-level cache keyed by `ticker:days`.
 * Returns a map { TICKER → closes[] } in date-ascending order.
 */
export function useSparklineSeries(tickers: string[], days = 30) {
  const [series, setSeries] = useState<Record<string, number[]>>({});

  useEffect(() => {
    const unique = Array.from(
      new Set((tickers || []).filter(Boolean).map((t) => t.toUpperCase())),
    );
    if (unique.length === 0) {
      setSeries({});
      return;
    }

    const cacheKey = (t: string) => `${t}:${days}`;
    const missing = unique.filter((t) => !cache.has(cacheKey(t)));
    const initial: Record<string, number[]> = {};
    unique.forEach((t) => {
      const hit = cache.get(cacheKey(t));
      if (hit) initial[t] = hit;
    });
    setSeries(initial);

    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const sinceStr = since.toISOString().slice(0, 10);

      const { data, error } = await supabase
        .from('daily_prices')
        .select('ticker, date, close')
        .in('ticker', missing)
        .gte('date', sinceStr)
        .order('date', { ascending: true });

      if (cancelled || error || !data) return;

      const grouped: Record<string, number[]> = {};
      for (const row of data) {
        const t = (row.ticker as string).toUpperCase();
        const c = row.close as number | null;
        if (c == null) continue;
        (grouped[t] ||= []).push(Number(c));
      }

      missing.forEach((t) => {
        cache.set(cacheKey(t), grouped[t] || []);
      });

      setSeries((prev) => ({ ...prev, ...grouped }));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickers.join('|'), days]);

  return series;
}

/**
 * Same as useSparklineSeries, but returns `{ date, close }` pairs so that
 * date-aware overlays (e.g. a ReferenceLine on a trade date) can align
 * precisely with the underlying x-axis values. Critical for charts that
 * use a date-typed axis — Recharts defaults to a category axis, and a
 * ReferenceLine with a non-matching x value silently falls in the wrong
 * place rather than failing loudly.
 */
export function useSparklineSeriesDated(ticker: string, days = 90) {
  const [series, setSeries] = useState<Array<{ date: string; close: number }>>([]);

  useEffect(() => {
    const t = (ticker || '').toUpperCase();
    if (!t) {
      setSeries([]);
      return;
    }

    const cacheKey = `${t}:${days}`;
    const cached = datedCache.get(cacheKey);
    if (cached) {
      setSeries(cached);
      return;
    }

    let cancelled = false;
    (async () => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const sinceStr = since.toISOString().slice(0, 10);

      const { data, error } = await supabase
        .from('daily_prices')
        .select('date, close')
        .eq('ticker', t)
        .gte('date', sinceStr)
        .order('date', { ascending: true });

      if (cancelled || error || !data) return;

      const points = data
        .filter((r) => r.close != null)
        .map((r) => ({ date: String(r.date), close: Number(r.close) }));

      datedCache.set(cacheKey, points);
      setSeries(points);
    })();

    return () => {
      cancelled = true;
    };
  }, [ticker, days]);

  return series;
}
